// Enhanced Push Notification Utility for Lala Land PWA
// Supports both browser push notifications and in-app notifications

const API = process.env.REACT_APP_BACKEND_URL;

// VAPID public key for push subscriptions (matches backend)
const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

// Convert VAPID key for subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

// Check current permission status
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

// Register service worker
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    
    console.log('Service Worker registered:', registration.scope);
    
    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;
    
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

// Subscribe to push notifications
export const subscribeToPush = async (token) => {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }

  const permission = await requestNotificationPermission();
  if (!permission) {
    throw new Error('Notification permission denied');
  }

  const registration = await navigator.serviceWorker.ready;
  
  // Check for existing subscription
  let subscription = await registration.pushManager.getSubscription();
  
  if (!subscription) {
    // Create new subscription
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    });
  }

  // Send subscription to backend
  const response = await fetch(`${API}/api/notifications/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      subscription: subscription.toJSON()
    })
  });

  if (!response.ok) {
    throw new Error('Failed to save subscription');
  }

  return subscription;
};

// Unsubscribe from push notifications
export const unsubscribeFromPush = async (token) => {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  
  if (subscription) {
    await subscription.unsubscribe();
  }

  // Remove from backend
  await fetch(`${API}/api/notifications/unsubscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  return true;
};

// Check if subscribed to push
export const isPushSubscribed = async () => {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
};

// Get notification settings from backend
export const getNotificationSettings = async (token) => {
  const response = await fetch(`${API}/api/notifications/settings`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to get notification settings');
  }
  
  return response.json();
};

// Update notification settings
export const updateNotificationSettings = async (token, settings) => {
  const response = await fetch(`${API}/api/notifications/settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(settings)
  });
  
  if (!response.ok) {
    throw new Error('Failed to update notification settings');
  }
  
  return response.json();
};

// Get in-app notifications
export const getInAppNotifications = async (token, limit = 20, unreadOnly = false) => {
  const params = new URLSearchParams({ limit, unread_only: unreadOnly });
  const response = await fetch(`${API}/api/notifications/in-app?${params}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to get notifications');
  }
  
  return response.json();
};

// Get unread notification count
export const getUnreadCount = async (token) => {
  const response = await fetch(`${API}/api/notifications/unread-count`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    return 0;
  }
  
  const data = await response.json();
  return data.count || 0;
};

// Mark notification as read
export const markNotificationRead = async (token, notificationId) => {
  const response = await fetch(`${API}/api/notifications/${notificationId}/read`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  return response.ok;
};

// Mark all notifications as read
export const markAllNotificationsRead = async (token) => {
  const response = await fetch(`${API}/api/notifications/mark-all-read`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  return response.ok;
};

// Delete notification
export const deleteNotification = async (token, notificationId) => {
  const response = await fetch(`${API}/api/notifications/${notificationId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  return response.ok;
};

// Send local notification (when app is in foreground)
export const sendLocalNotification = (title, options = {}) => {
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      ...options
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
      if (options.onClick) {
        options.onClick();
      }
    };

    return notification;
  }
  return null;
};

// Test push notification (for debugging)
export const testPushNotification = async (token) => {
  const response = await fetch(`${API}/api/notifications/test`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  return response.json();
};

// Initialize push notifications
export const initializePushNotifications = async (token) => {
  try {
    // Register service worker
    await registerServiceWorker();
    
    // Check if already subscribed
    const isSubscribed = await isPushSubscribed();
    
    if (!isSubscribed && Notification.permission === 'granted') {
      // Auto-subscribe if permission already granted
      await subscribeToPush(token);
    }
    
    return { success: true, subscribed: isSubscribed };
  } catch (error) {
    console.error('Failed to initialize push notifications:', error);
    return { success: false, error: error.message };
  }
};
